// ───────────────────────────────────────────────
// Global "Log arrows" sheet (#1943). One app-level ArrowLogSheet instance,
// openable from anywhere (Home quick action, Sidebar nav item, History
// kebab-edit) so the entry point no longer dictates a page-local mount.
//
// Deliberately NOT global: PostRoundNudge keeps its own local sheet — see the
// comment in components/PostRoundNudge.jsx.
//
// API (via useArrowLog()):
//   openBlank(opts?)  — open empty; opts may carry {initial, archerId} overrides
//   openEdit(entry)   — open pre-filled from an existing arrow-log entry,
//                       saving PATCHes that entry
//   subscribe(fn)     — fn({type: "saved", entry}) fires after every successful
//                       save through the global sheet; returns an unsubscribe.
//                       (No "deleted" events: the sheet cannot delete — History's
//                       kebab delete is the only deleter and updates its own
//                       list directly.)
//   isOpen            — whether the global sheet is currently up (Home uses it
//                       to suspend its navigation shortcuts under the sheet)
// ───────────────────────────────────────────────
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { ArrowLogSheet } from "../components/ArrowLogSheet";
import { useAuth } from "./auth";

const ArrowLogContext = createContext(null);

export function ArrowLogProvider({ children }) {
  const { activeArcher } = useAuth();
  // null = closed; {} = blank entry; {editId, initial} = edit — the same shape
  // History's page-local sheet state used before the lift (#1812 → #1943).
  const [sheet, setSheet] = useState(null);
  const listenersRef = useRef(new Set());

  const subscribe = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => {
      listenersRef.current.delete(fn);
    };
  }, []);

  const openBlank = useCallback((opts = {}) => setSheet({ ...opts }), []);

  const openEdit = useCallback((entry) => {
    setSheet({
      editId: entry.id,
      initial: {
        date: entry.date,
        count: entry.count,
        activity: entry.activity,
        label: entry.label ?? "",
      },
    });
  }, []);

  const close = useCallback(() => setSheet(null), []);

  const handleSaved = useCallback((saved) => {
    for (const fn of listenersRef.current) fn({ type: "saved", entry: saved });
  }, []);

  const isOpen = sheet !== null;

  const value = useMemo(
    () => ({ openBlank, openEdit, subscribe, isOpen }),
    [openBlank, openEdit, subscribe, isOpen],
  );

  return (
    <ArrowLogContext.Provider value={value}>
      {children}
      <ArrowLogSheet
        open={isOpen}
        onClose={close}
        onSaved={handleSaved}
        initial={sheet?.initial}
        editId={sheet?.editId}
        archerId={sheet?.archerId ?? activeArcher?.id}
      />
    </ArrowLogContext.Provider>
  );
}

export function useArrowLog() {
  const ctx = useContext(ArrowLogContext);
  if (ctx == null) {
    throw new Error("useArrowLog must be used within an <ArrowLogProvider>");
  }
  return ctx;
}
